import React from 'react';
import { X } from 'lucide-react';

interface CollaboratorItemProps {
  id: number;
  userName: string;
  userEmail: string;
  role: 'owner' | 'collaborator' | 'viewer';
  addedAt: string;
  isOwner: boolean;
  onRemove: (id: number) => void;
  isRemoving?: boolean;
}

const CollaboratorItem: React.FC<CollaboratorItemProps> = ({
  id,
  userName,
  userEmail,
  role,
  addedAt,
  isOwner,
  onRemove,
  isRemoving = false,
}) => {
  const formatDate = (dateStr: string | Date) => {
    try {
      let date: Date;
      if (typeof dateStr === 'string') {
        date = new Date(dateStr);
      } else {
        date = dateStr;
      }

      if (isNaN(date.getTime())) return 'Recently added';

      // Format as "Jun 8, 2026"
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Recently added';
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow" title={`Added ${formatDate(addedAt)}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
            {userName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">{userName}</div>
            <div className="text-xs text-gray-500 truncate">{userEmail}</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 ml-2 shrink-0">
        <span
          className={`text-xs font-medium px-2 py-1 rounded capitalize ${
            role === 'owner'
              ? 'text-blue-700 bg-blue-100'
              : 'text-gray-600 bg-gray-100'
          }`}
        >
          {role}
        </span>
        {!isOwner && (
          <button
            onClick={() => onRemove(id)}
            disabled={isRemoving}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Remove collaborator"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default CollaboratorItem;
